import { Component } from '@angular/core'
import { ActivatedRoute, ParamMap } from '@angular/router'
import { PagedResult } from '@ftr/contracts/api/core/response/PagedResult'
import { ApiResult, ArrayUtils, PageStyle, ScreenSizeService, fade } from '@ftr/foundation'
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs'
import { ComplexFilterData, ComplexFilterSection } from '../../complex-filter/ComplexFilter'
import { ListComponent } from '../../list'
import { REQUEST_DEBOUNCE_MS, SMALL_SCREEN_SIZES } from '../complex-list'

@Component({
  template: '',
  animations: [fade(500, 0)],
})
export abstract class ComplexListComponent<T> extends ListComponent<T> {
  /**
   * Passed to the complex filter, so it knows when to show or hide sections.
   * Also used to close the filter component when applied on a small screen
   */
  protected isSmallScreen: boolean
  /**
   * The number of filters applied to the list, excluding sort
   */
  protected numberOfAppliedFilters: number
  protected filterConfig: ComplexFilterSection[]
  protected readonly pageStyle = PageStyle.Fluid

  readonly toggleFiltersSubject = new BehaviorSubject<boolean>(false)
  readonly filterSubject = new ReplaySubject<ComplexFilterData>(1)

  protected constructor(
    route: ActivatedRoute,
    protected readonly screenSizeService: ScreenSizeService,
  ) {
    super(route)
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.watchScreenSize()
  }

  applyFilters(filters: ComplexFilterData): void {
    this.filterSubject.next(filters)
    if (this.isSmallScreen) {
      this.toggleFilters(false)
    }
  }

  clearFilters(filters: ComplexFilterData): void {
    this.filterSubject.next(filters)
  }

  toggleFilters = (toggle: boolean): void => {
    this.toggleFiltersSubject.next(toggle)
  }

  watchScreenSize(): void {
    this.screenSizeService.size.pipe(takeUntil(this.finalize)).subscribe(size => {
      this.isSmallScreen = SMALL_SCREEN_SIZES.includes(size)
    })
  }

  override getEntitiesObservable(
    _: Observable<ParamMap>,
    pageObservable: Observable<number>,
  ): ApiResult<PagedResult<T>> {
    return combineLatest([pageObservable, this.filterSubject]).pipe(
      // Debounce to prevent button spam
      debounceTime(REQUEST_DEBOUNCE_MS),
      distinctUntilChanged(ArrayUtils.shallowEquals),
      switchMap(([page, filters]) => this.fetchEntities(page, filters)),
    )
  }

  abstract fetchEntities(page: number, filters: ComplexFilterData): ApiResult<PagedResult<T>>
}
